import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import '../styles/EmlakPortfoy.css';
import { createResizedPair } from '../utils/imageResize';
import { useTranslation } from 'react-i18next';

const formatMoneyInput = (value: string): string => {
  const numbers = value.replace(/[^\d]/g, '');
  if (numbers === '') return '';
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => {
  return value.replace(/\./g, '');
};

interface EmlakFotograf {
  fotoID: number;
  portfoyID: number;
  dosyaYolu: string;
  isVitrin: boolean;
}

interface EmlakIletisim {
  iletisimID: number;
  adSoyad: string;
  telefon: string;
  eposta: string;
}

interface EmlakOzellik {
  ozellikID: number;
  ozellikAdi: string;
}

interface FormData {
  baslik: string;
  adres: string;
  aciklama: string;
  fiyat: string;
  m2: string;
  tur: string;
  durum: string;
  aktifMi: boolean;
}

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'],
    ['clean']
  ]
};

const EmlakPortfoyForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isEdit = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    baslik: '',
    adres: '',
    aciklama: '',
    fiyat: '',
    m2: '',
    tur: 'Konut',
    durum: 'Satilik',
    aktifMi: true
  });

  const [iletisimler, setIletisimler] = useState<EmlakIletisim[]>([]);
  const [ozellikler, setOzellikler] = useState<EmlakOzellik[]>([]);
  const [yeniOzellik, setYeniOzellik] = useState('');
  const [fotograflar, setFotograflar] = useState<EmlakFotograf[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portfoyID, setPortfoyID] = useState<number | null>(null);
  const [ilanNo, setIlanNo] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchPortfoy(parseInt(id));
    }
  }, [id]);

  const fetchPortfoy = async (portfoyId: number) => {
    try {
      const response = await axiosInstance.get(`/api/EmlakPortfoy/${portfoyId}`);
      const p = response.data;
      setPortfoyID(p.portfoyID);
      setIlanNo(p.ilanNo);
      setFormData({
        baslik: p.baslik || '',
        adres: p.adres || '',
        aciklama: p.aciklama || '',
        fiyat: p.fiyat ? formatMoneyInput(p.fiyat.toString()) : '',
        m2: p.m2?.toString() || '',
        tur: p.tur,
        durum: p.durum,
        aktifMi: p.aktifMi
      });
      setIletisimler(p.iletisimler || []);
      setOzellikler(p.ozellikler || []);
      setFotograflar(p.fotograflar || []);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
      navigate('/emlak-portfoy');
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // İletişim CRUD
  const addIletisim = () => {
    setIletisimler(prev => [...prev, { iletisimID: 0, adSoyad: '', telefon: '', eposta: '' }]);
  };

  const updateIletisim = (index: number, field: keyof EmlakIletisim, value: string) => {
    setIletisimler(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeIletisim = (index: number) => {
    setIletisimler(prev => prev.filter((_, i) => i !== index));
  };

  // Özellik CRUD
  const addOzellik = () => {
    if (!yeniOzellik.trim()) return;
    setOzellikler(prev => [...prev, { ozellikID: 0, ozellikAdi: yeniOzellik.trim() }]);
    setYeniOzellik('');
  };

  const removeOzellik = (index: number) => {
    setOzellikler(prev => prev.filter((_, i) => i !== index));
  };

  // Fotoğraf yükleme
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    if (!portfoyID && !isEdit) {
      toast.info(t('msg.onayGerekli'));
      return;
    }

    const targetId = portfoyID || (id ? parseInt(id) : null);
    if (!targetId) return;

    setUploading(true);
    const formDataUpload = new FormData();

    for (const file of Array.from(e.target.files)) {
      const { mainFile, thumbFile } = await createResizedPair(file);
      formDataUpload.append('files', mainFile);
      formDataUpload.append('thumbFiles', thumbFile);
    }

    try {
      const response = await axiosInstance.post(`/api/EmlakPortfoy/${targetId}/foto`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFotograflar(prev => [...prev, ...response.data]);
      toast.success(t('msg.islemBasarili'));
    } catch (error: any) {
      toast.error(error.response?.data  || t('msg.islemHatasi'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetVitrin = async (fotoId: number) => {
    try {
      await axiosInstance.put(`/api/EmlakPortfoy/foto/${fotoId}/vitrin`);
      setFotograflar(prev => prev.map(f => ({ ...f, isVitrin: f.fotoID === fotoId })));
      toast.success(t('msg.islemBasarili'));
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleDeleteFoto = async (fotoId: number) => {
    try {
      await axiosInstance.delete(`/api/EmlakPortfoy/foto/${fotoId}`);
      setFotograflar(prev => prev.filter(f => f.fotoID !== fotoId));
      toast.success(t('msg.islemBasarili'));
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    }
  };

  // Form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.baslik.trim()) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    if (!formData.fiyat || parseFloat(parseMoneyInput(formData.fiyat)) <= 0) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }
    if (!formData.m2 || parseInt(formData.m2) <= 0) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(isEdit && id ? { portfoyID: parseInt(id) } : {}),
        baslik: formData.baslik.trim(),
        adres: formData.adres.trim(),
        aciklama: formData.aciklama,
        fiyat: parseFloat(parseMoneyInput(formData.fiyat)),
        m2: parseInt(formData.m2),
        tur: formData.tur,
        durum: formData.durum,
        aktifMi: formData.aktifMi,
        iletisimler: iletisimler.filter(i => i.adSoyad.trim()).map(i => ({
          adSoyad: i.adSoyad.trim(),
          telefon: i.telefon?.trim() || null,
          eposta: i.eposta?.trim() || null,
          portfoyID: isEdit && id ? parseInt(id) : 0
        })),
        ozellikler: ozellikler.map(o => ({
          ozellikAdi: o.ozellikAdi,
          portfoyID: isEdit && id ? parseInt(id) : 0
        }))
      };

      if (isEdit && id) {
        await axiosInstance.put(`/api/EmlakPortfoy/${id}`, payload);
        toast.success(t('msg.islemBasarili'));
        navigate(`/emlak-portfoy/${id}`);
      } else {
        const response = await axiosInstance.post('/api/EmlakPortfoy', payload);
        const newId = response.data.portfoyID;
        setPortfoyID(newId);
        toast.success(t('msg.islemBasarili'));
        navigate(`/emlak-portfoy/duzenle/${newId}`);
      }
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ep-container">
      <div className="ep-header">
        <div className="ep-header-left">
          <button className="ep-btn-back" onClick={() => navigate('/emlak-portfoy')}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="ep-title">
            <i className="fas fa-building"></i>
            {isEdit ? 'İlanı Düzenle' : 'Yeni İlan'}
          </h1>
          {isEdit && ilanNo && <span className="ep-count">{ilanNo}</span>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ep-form-grid">
          {/* Sol: Ana bilgiler */}
          <div className="ep-form-main">
            {/* Temel Bilgiler */}
            <div className="ep-form-section">
              <h2 className="ep-form-section-title">
                <i className="fas fa-info-circle"></i> Temel Bilgiler
              </h2>
              <div className="ep-form-row">
                <div className="ep-form-group ep-form-full">
                  <label className="ep-label">Başlık *</label>
                  <input type="text" className="ep-input" value={formData.baslik} onChange={e => handleChange('baslik', e.target.value)} placeholder={t('label.ara')} required />
                </div>
              </div>
              <div className="ep-form-row">
                <div className="ep-form-group">
                  <label className="ep-label">Fiyat (₺) *</label>
                  <input type="text" className="ep-input" value={formData.fiyat} onChange={e => handleChange('fiyat', formatMoneyInput(e.target.value))} placeholder="0" required />
                </div>
                <div className="ep-form-group">
                  <label className="ep-label">m² *</label>
                  <input type="number" className="ep-input" value={formData.m2} onChange={e => handleChange('m2', e.target.value)} placeholder="0" min="0" required />
                </div>
              </div>
              <div className="ep-form-row">
                <div className="ep-form-group">
                  <label className="ep-label">{t('label.tur')}</label>
                  <select className="ep-input" value={formData.tur} onChange={e => handleChange('tur', e.target.value)}>
                    <option value="Konut">{t('emlak.konut')}</option>
                    <option value="IsYeri">{t('emlak.isYeri')}</option>
                    <option value="Arsa">Arsa</option>
                    <option value="Tarla">Tarla</option>
                  </select>
                </div>
                <div className="ep-form-group">
                  <label className="ep-label">Durum</label>
                  <select className="ep-input" value={formData.durum} onChange={e => handleChange('durum', e.target.value)}>
                    <option value="Satilik">Satılık</option>
                    <option value="Kiralik">Kiralık</option>
                    <option value="Portfoy">Portföy</option>
                  </select>
                </div>
              </div>
              <div className="ep-form-row">
                <div className="ep-form-group ep-form-full">
                  <label className="ep-label">Adres</label>
                  <input type="text" className="ep-input" value={formData.adres} onChange={e => handleChange('adres', e.target.value)} placeholder={t('label.ara')} />
                </div>
              </div>
            </div>

            {/* Açıklama */}
            <div className="ep-form-section">
              <h2 className="ep-form-section-title">
                <i className="fas fa-align-left"></i> Açıklama
              </h2>
              <div className="ep-quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={formData.aciklama}
                  onChange={(value) => handleChange('aciklama', value)}
                  modules={quillModules}
                  placeholder={t('label.aciklama')}
                />
              </div>
            </div>

            {/* Fotoğraflar */}
            <div className="ep-form-section">
              <h2 className="ep-form-section-title">
                <i className="fas fa-camera"></i> Fotoğraflar
                {!isEdit && !portfoyID && <span className="ep-hint-text"> (Önce ilanı kaydedin)</span>}
              </h2>
              <div className="ep-foto-upload-area" onClick={() => (isEdit || portfoyID) && fileInputRef.current?.click()} style={{ opacity: (!isEdit && !portfoyID) ? 0.5 : 1, cursor: (!isEdit && !portfoyID) ? 'not-allowed' : 'pointer' }}>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
                {uploading ? (
                  <><i className="fas fa-spinner fa-spin"></i><span>{t('msg.yukleniyor')}</span></>
                ) : (
                  <><i className="fas fa-cloud-upload-alt"></i><span>Fotoğraf yüklemek için tıklayın</span><p>JPG, PNG, WEBP · Maks. 10MB</p></>
                )}
              </div>
              {fotograflar.length > 0 && (
                <div className="ep-foto-grid">
                  {fotograflar.map(foto => {
                    const thumbYolu = foto.dosyaYolu.replace(/(\.[^.]+)$/, 'x250$1');
                    return (
                    <div key={foto.fotoID} className={`ep-foto-item ${foto.isVitrin ? 'ep-foto-vitrin' : ''}`}>
                      <img src={`${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${thumbYolu}`}
                           onError={ev => { (ev.target as HTMLImageElement).src = `${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${foto.dosyaYolu}`; }}
                           alt="" />
                      <div className="ep-foto-actions">
                        <button type="button" className={`ep-foto-star ${foto.isVitrin ? 'active' : ''}`} onClick={() => handleSetVitrin(foto.fotoID)} title="Vitrin fotoğrafı yap">
                          <i className={`fa${foto.isVitrin ? 's' : 'r'} fa-star`}></i>
                        </button>
                        <button type="button" className="ep-foto-delete" onClick={() => handleDeleteFoto(foto.fotoID)} title={t('btn.sil')}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                      {foto.isVitrin && <span className="ep-foto-vitrin-badge">Vitrin</span>}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sağ: Yan panel */}
          <div className="ep-form-sidebar">
            {/* İletişim Kişileri */}
            <div className="ep-form-section">
              <h2 className="ep-form-section-title">
                <i className="fas fa-address-book"></i> İlgili Kişiler
              </h2>
              {iletisimler.map((item, index) => (
                <div key={index} className="ep-iletisim-card">
                  <button type="button" className="ep-iletisim-remove" onClick={() => removeIletisim(index)}>
                    <i className="fas fa-times"></i>
                  </button>
                  <input type="text" className="ep-input ep-input-sm" placeholder="Ad Soyad *" value={item.adSoyad} onChange={e => updateIletisim(index, 'adSoyad', e.target.value)} />
                  <input type="text" className="ep-input ep-input-sm" placeholder={t('label.telefon')} value={item.telefon} onChange={e => updateIletisim(index, 'telefon', e.target.value)} />
                  <input type="email" className="ep-input ep-input-sm" placeholder={t('label.eposta')} value={item.eposta} onChange={e => updateIletisim(index, 'eposta', e.target.value)} />
                </div>
              ))}
              <button type="button" className="ep-btn-add" onClick={addIletisim}>
                <i className="fas fa-plus"></i> Kişi Ekle
              </button>
            </div>

            {/* Özellikler */}
            <div className="ep-form-section">
              <h2 className="ep-form-section-title">
                <i className="fas fa-tags"></i> Özellikler
              </h2>
              <div className="ep-ozellik-input-row">
                <input type="text" className="ep-input ep-input-sm" placeholder={t('label.ara')} value={yeniOzellik} onChange={e => setYeniOzellik(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOzellik(); } }} />
                <button type="button" className="ep-btn-add-sm" onClick={addOzellik}><i className="fas fa-plus"></i></button>
              </div>
              <div className="ep-ozellik-tags">
                {ozellikler.map((oz, index) => (
                  <span key={index} className="ep-ozellik-tag">
                    {oz.ozellikAdi}
                    <button type="button" onClick={() => removeOzellik(index)}><i className="fas fa-times"></i></button>
                  </span>
                ))}
              </div>
            </div>

            {/* Durum */}
            <div className="ep-form-section">
              <label className="ep-checkbox-label">
                <input type="checkbox" checked={formData.aktifMi} onChange={e => handleChange('aktifMi', e.target.checked)} />
                <span>Aktif İlan</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="ep-form-footer">
          <button type="button" className="ep-btn-cancel" onClick={() => navigate('/emlak-portfoy')}>{t('btn.iptal')}</button>
          <button type="submit" className="ep-btn-primary" disabled={saving}>
            {saving ? <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</> : <><i className="fas fa-save"></i> Kaydet</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmlakPortfoyForm;
